import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './ContractWizard.css';
import { FORM_TYPE_CATALOG, OTHER_FORM_CATALOG, catalogGroupsFromPayload, formsForParent } from '../utils/formTypeIds';
import { fetchFormCatalog } from '../utils/childFormApi';
import { formGenerationUpgradeMessage } from '../utils/formGenerationAccess';

export const OTHER_FORM_FOLD_COUNT = 4;

export function sortFormsByLabel(forms) {
  return [...(forms || [])].sort((left, right) => (
    String(left.label || '').localeCompare(String(right.label || ''), undefined, { sensitivity: 'base' })
  ));
}

export function filterFormsByQuery(forms, query) {
  const needle = String(query || '').trim().toLowerCase();
  if (!needle) return forms || [];
  return (forms || []).filter((form) => (
    [form.label, form.description, form.formType]
      .some((part) => String(part || '').toLowerCase().includes(needle))
  ));
}

export function foldHeightFromList(listEl, foldCount = OTHER_FORM_FOLD_COUNT) {
  if (!listEl) return null;
  const buttons = listEl.querySelectorAll('[data-testid="other-form-option"]');
  const fold = Array.from(buttons).slice(0, foldCount);
  if (fold.length < foldCount) return null;
  const last = fold[fold.length - 1];
  const listBox = listEl.getBoundingClientRect();
  const lastBox = last.getBoundingClientRect();
  const height = lastBox.bottom - listBox.top + (listEl.scrollTop || 0);
  return height > 0 ? height : null;
}

function FormButton({ form, onSelect, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => onSelect(form.formType)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '18px 20px',
        border: `2px solid ${form.color}20`,
        borderRadius: 10,
        background: `${form.color}08`,
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
      }}
    >
      <span style={{ fontSize: '2rem', lineHeight: 1 }}>{form.icon}</span>
      <div>
        <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1f2937', marginBottom: 3 }}>{form.label}</div>
        <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>{form.description}</div>
      </div>
      <span style={{ marginLeft: 'auto', color: '#9ca3af', fontSize: '1.2rem' }}>›</span>
    </button>
  );
}

const CreateFormModal = ({
  isOpen,
  onClose,
  onSelectForm,
  parentType = null,
  parentId = null,
  userEmail = null,
  getAccessTokenSilently,
  getIdTokenClaims,
  catalog: preloadedCatalog = null,
  startOnOther = false,
  upgradeRequired = false,
  currentPlanName = null,
  upgradePlanName = null,
}) => {
  const navigate = useNavigate();
  const [showOther, setShowOther] = useState(Boolean(parentType) || startOnOther);
  const [otherCatalog, setOtherCatalog] = useState(preloadedCatalog || []);
  const [catalogLoading, setCatalogLoading] = useState(!preloadedCatalog);
  const [query, setQuery] = useState('');
  const [foldHeight, setFoldHeight] = useState(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!isOpen || upgradeRequired) {
      setShowOther(Boolean(parentType) || startOnOther);
      setQuery('');
      return undefined;
    }
    setShowOther(Boolean(parentType) || startOnOther);
    setQuery('');
    if (preloadedCatalog) {
      setOtherCatalog(preloadedCatalog);
      setCatalogLoading(false);
      return undefined;
    }
    setCatalogLoading(true);
    let cancelled = false;
    fetchFormCatalog({ userEmail, parentType, parentId, getAccessTokenSilently, getIdTokenClaims }).then((payload) => {
      const groups = catalogGroupsFromPayload(payload);
      if (cancelled) return;
      if (groups.length) setOtherCatalog(groups);
      else if (parentType) setOtherCatalog([]);
      else setOtherCatalog(OTHER_FORM_CATALOG);
      setCatalogLoading(false);
    }).catch(() => {
      if (!cancelled) {
        setOtherCatalog(parentType ? [] : OTHER_FORM_CATALOG);
        setCatalogLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [isOpen, parentType, parentId, userEmail, getAccessTokenSilently, getIdTokenClaims, preloadedCatalog, startOnOther, upgradeRequired]);

  const resolvedCatalog = preloadedCatalog || otherCatalog;
  const showCatalogLoading = showOther && !preloadedCatalog && catalogLoading && !resolvedCatalog.length;

  const otherForms = useMemo(() => {
    const groups = parentType
      ? [{ group: 'this_deal', label: 'Forms for this deal', forms: formsForParent(parentType, resolvedCatalog) }]
      : resolvedCatalog;
    return groups.map((group) => ({
      ...group,
      forms: sortFormsByLabel(group.forms),
    }));
  }, [resolvedCatalog, parentType]);

  const visibleForms = useMemo(() => (
    otherForms.map((group) => ({
      ...group,
      forms: filterFormsByQuery(group.forms, query),
    })).filter((group) => group.forms.length)
  ), [otherForms, query]);

  const selectableCount = otherForms.reduce((count, group) => count + group.forms.length, 0);
  const showSearchAndScroll = selectableCount > OTHER_FORM_FOLD_COUNT;

  useLayoutEffect(() => {
    if (!showSearchAndScroll) {
      setFoldHeight(null);
      return undefined;
    }
    const measure = () => setFoldHeight(foldHeightFromList(listRef.current));
    measure();
    const node = listRef.current;
    const observer = typeof ResizeObserver !== 'undefined' && node
      ? new ResizeObserver(measure)
      : null;
    if (observer && node) observer.observe(node);
    window.addEventListener('resize', measure);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [isOpen, showOther, showSearchAndScroll, visibleForms]);

  if (!isOpen) return null;

  if (upgradeRequired) {
    return (
      <div className="wizard-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="wizard-container">
          <div className="wizard-header">
            <h2>Create New Form</h2>
            <button className="wizard-close" onClick={onClose}>×</button>
          </div>
          <div className="wizard-content" style={{ padding: '32px 24px' }}>
            <p style={{ textAlign: 'center', color: '#374151', marginTop: 0, marginBottom: 28, lineHeight: 1.6 }}>
              {formGenerationUpgradeMessage({ currentPlanName, upgradePlanName })}
            </p>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <button
                type="button"
                className="btn-primary"
                onClick={() => {
                  onClose();
                  navigate('/profile?tab=subscription');
                }}
              >
                Upgrade
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wizard-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="wizard-container">
        <div className="wizard-header">
          <h2>{showOther ? 'Other forms' : 'Create New Form'}</h2>
          <button className="wizard-close" onClick={onClose}>×</button>
        </div>
        <div className="wizard-content" style={{ padding: '32px 24px' }}>
          {!showOther && (
            <>
              <p style={{ textAlign: 'center', color: '#6b7280', marginTop: 0, marginBottom: 28 }}>
                Which form would you like to create?
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {FORM_TYPE_CATALOG.map((form) => (
                  <FormButton
                    key={form.formType}
                    form={form}
                    onSelect={(type) => onSelectForm(type, form)}
                  />
                ))}
                <FormButton
                  form={{
                    formType: '__other__',
                    label: 'Other',
                    description: 'Seller/buyer counters, addenda, and more',
                    icon: '⋯',
                    color: '#6b7280',
                  }}
                  onSelect={() => setShowOther(true)}
                />
              </div>
            </>
          )}
          {showOther && showCatalogLoading && (
            <p style={{ textAlign: 'center', color: '#6b7280', margin: 0 }}>
              Loading available forms…
            </p>
          )}
          {showOther && !showCatalogLoading && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {!parentType && (
                <button type="button" onClick={() => setShowOther(false)} style={{ alignSelf: 'flex-start' }}>
                  ← Back
                </button>
              )}
              {showSearchAndScroll && (
                <input
                  type="search"
                  aria-label="Search forms"
                  placeholder="Search forms"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: 8,
                    fontSize: '0.95rem',
                  }}
                />
              )}
              <div
                ref={listRef}
                data-testid="other-form-list"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 22,
                  ...(showSearchAndScroll ? {
                    overflowY: 'auto',
                    paddingRight: 4,
                    ...(foldHeight ? { maxHeight: foldHeight } : {}),
                  } : {}),
                }}
              >
                {visibleForms.map((group) => (
                  <div key={group.group}>
                    <h3 style={{ margin: '0 0 10px', fontSize: '0.95rem', color: '#374151' }}>{group.label}</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {group.forms.map((form) => (
                        <FormButton
                          key={form.formType}
                          form={form}
                          testId="other-form-option"
                          onSelect={(type) => onSelectForm(type, form)}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateFormModal;
